import 'katex/dist/katex.min.css'

import type { AnyExtension, Editor, Extensions, JSONContent } from '@tiptap/core'
import { generateHTML, generateJSON as generateJsonDefault, generateText as generateTextDefault } from '@tiptap/core'
// import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
// import Link from '@tiptap/extension-link'
// import Placeholder from '@tiptap/extension-placeholder'
// import TaskItem from '@tiptap/extension-task-item'
// import TaskList from '@tiptap/extension-task-list'
// import Align from '@tiptap/extension-text-align'
// import TextStyle from '@tiptap/extension-text-style'
// import UnderLine from '@tiptap/extension-underline'
import { ListKit } from '@tiptap/extension-list'
import { Mathematics } from '@tiptap/extension-mathematics'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyleKit } from '@tiptap/extension-text-style'
import { Placeholder } from '@tiptap/extensions'
import StarterKit from '@tiptap/starter-kit'
import katex from 'katex'
import { isEmpty } from 'rambda'

import { CurrentValueMark } from '../FormulaView/FormulaEditor/Mark/currentValue'
import { CustomizeCodeBlock } from './CustomizeCodeBlock'
import { SegmentDistance } from './SegmentDistance'
import {
    EditableTable,
    EditableTableCell,
    EditableTableHeader,
    EditableTableRow,
    ReadonlyTable,
    ReadonlyTableCell,
    ReadonlyTableHeader,
    ReadonlyTableRow,
    TablePaste
} from './Table'
import type { TiptapExtensionMode } from './types'
import type { UploadImageOptions } from './UploadImage'
import { UploadImage } from './UploadImage'
import { UploadImagePlaceholder } from './UploadImage/UploadImagePlaceholder'
import type { UploadVideoOptions } from './UploadVideo'
import { UploadVideo } from './UploadVideo'
import { UploadVideoPlaceholder } from './UploadVideo/UploadVideoPlaceholder'
import type { VariableExtensionOptions } from './VariableBlock'
import { VariableBlock } from './VariableBlock'

export interface ExtensionsOptions {
    mode?: TiptapExtensionMode
    image?: Partial<UploadImageOptions>
    video?: Partial<UploadVideoOptions>
    variable?: Partial<VariableExtensionOptions>
    placeholder?: string | (() => string)
    currentValueMark?: boolean
    readonly?: boolean
}

const DEFAULT_PLACEHOLDER = '输入内容'

/** 刷新 Placeholder decoration。文案由 configure 时传入的函数从外部 ref 读取。 */
export function refreshEditorPlaceholder(editor: Editor | null | undefined) {
    if (!editor || editor.isDestroyed || !editor.view || editor.view.isDestroyed) {
        return
    }
    editor.view.dispatch(editor.state.tr.setMeta('refreshPlaceholder', true))
}

export function getExtensions(options?: ExtensionsOptions): Extensions {
    const { mode = 'pure-text', image, video, variable, placeholder, currentValueMark, readonly } = options ?? {}

    const isFullMode = mode === 'all'
    const isPureTextMode = mode === 'pure-text'
    const isReadonly = Boolean(readonly)
    const textAlignTypes = isFullMode
        ? ['heading', 'paragraph', 'image', 'video', 'tableCell', 'tableHeader']
        : ['heading', 'paragraph', 'image', 'video']

    return [
        Placeholder.configure({
            emptyEditorClass: 'editor-placeholder',
            placeholder: typeof placeholder === 'function' ? () => placeholder() || DEFAULT_PLACEHOLDER : placeholder || DEFAULT_PLACEHOLDER
        }),
        StarterKit.configure({
            // Duplicate with ListKit
            bulletList: false,
            listItem: false,
            listKeymap: false,
            orderedList: false,

            codeBlock: false,
            heading: isFullMode
                ? {
                      levels: [1, 2, 3]
                  }
                : false,
            blockquote: isFullMode ? undefined : false,
            horizontalRule: isFullMode ? undefined : false,
            link: isPureTextMode ? false : undefined
        }),

        variable && VariableBlock.configure(variable),

        currentValueMark && CurrentValueMark,

        ...(isFullMode ? [UploadImage.configure({ ...image }), UploadImagePlaceholder] : []),
        ...(isFullMode ? [UploadVideo.configure({ ...video }), UploadVideoPlaceholder] : []),
        ...(isFullMode
            ? [
                  isReadonly ? ReadonlyTable : EditableTable,
                  isReadonly ? ReadonlyTableRow : EditableTableRow,
                  isReadonly ? ReadonlyTableHeader : EditableTableHeader,
                  isReadonly ? ReadonlyTableCell : EditableTableCell,
                  TablePaste
              ]
            : []),

        isFullMode && ListKit.configure(),
        isFullMode && CustomizeCodeBlock,

        !isPureTextMode && TextStyleKit,
        !isPureTextMode && TextAlign.configure({ types: textAlignTypes }),
        !isPureTextMode && SegmentDistance.configure({ types: ['paragraph', 'heading'] }),
        !isPureTextMode && Mathematics,
        !isPureTextMode && Highlight.configure({ multicolor: true })
    ].filter(Boolean) as AnyExtension[]
}

export const generateText = (doc?: JSONContent, options?: ExtensionsOptions) => {
    if (isEmpty(doc) || !doc) {
        return ''
    }
    return generateTextDefault(doc, getExtensions({ mode: 'all', ...options }))
}

export const generateJson = (html: string, options?: ExtensionsOptions) =>
    generateJsonDefault(html, getExtensions({ mode: 'all', ...options }))

const CONTENT_NODES = new Set(['image', 'video', 'latex', 'iframe', 'table', 'variable'])

function hasVisibleContent(node: JSONContent) {
    if (!node || !node.type) return false

    if (node.type === 'text') {
        return !!node.text?.trim()
    }

    if (CONTENT_NODES.has(node.type)) {
        return true
    }

    if (!node.content) return false

    return node.content.some(hasVisibleContent)
}

/**
 * 检查富文本内容是否为空节点内容
 * @param {JSONContent} doc
 * @returns {boolean}
 */
export const isEmptyRichTextValue = (doc: JSONContent, options?: ExtensionsOptions) => {
    return !hasVisibleContent(doc)
}

/**
 * 将富文本 JSON 预编译为带变量占位符的 HTML 字符串。
 * 变量节点保留为 <variable data-value="..."> 供运行时替换，
 * 数学公式节点在此处直接渲染为 KaTeX HTML，避免运行时重复解析。
 * 调用方：BlockSettings onChange，结果存入 config.content.htmlValue。
 */
export function generatehtmlValue(json: JSONContent | null | undefined, options?: ExtensionsOptions): string {
    if (!json) return ''

    const extensions = getExtensions({ mode: 'all', variable: {}, readonly: true, ...options })
    const rawHtml = generateHTML(json, extensions)

    if (!rawHtml.includes('data-type="inline-math"') && !rawHtml.includes('data-type="block-math"')) {
        return rawHtml
    }

    const doc = new DOMParser().parseFromString(rawHtml, 'text/html')

    doc.querySelectorAll<HTMLElement>('span[data-type="inline-math"][data-latex]').forEach(el => {
        const latex = el.getAttribute('data-latex') ?? ''
        el.className = 'tiptap-mathematics-render'
        el.innerHTML = katex.renderToString(latex, { throwOnError: false })
    })

    doc.querySelectorAll<HTMLElement>('div[data-type="block-math"][data-latex]').forEach(el => {
        const latex = el.getAttribute('data-latex') ?? ''
        el.className = 'tiptap-mathematics-render'
        el.innerHTML = katex.renderToString(latex, { throwOnError: false, displayMode: true })
    })

    return doc.body.innerHTML
}

const UPLOAD_PLACEHOLDER_TYPES = ['uploadImagePlaceholder', 'uploadVideoPlaceholder'] as const

/**
 * 从富文本 JSON 中移除上传占位节点，得到可安全持久化的内容。
 *
 * - 移除类型：uploadImagePlaceholder、uploadVideoPlaceholder（历史遗留或兼容解析产生的占位节点）。
 * - 使用处：TiptapEditor 已在 onChange 中统一调用，凡通过 onChange 拿到的内容均为过滤后的结果，无需在业务层再调。
 * - 手动调用：若通过 editor.getJSON() 等直接取内容再保存/提交，建议先经本函数过滤再持久化，避免占位节点写入库。
 *
 * @param doc 富文本 JSON（如 editor.getJSON() 或 onChange 收到的值）
 * @returns 过滤后的 JSON，null/undefined 入则出不变
 */
export function filterUploadPlaceholdersFromJSON(doc: JSONContent | null | undefined): JSONContent | null | undefined {
    if (!doc) return doc

    const filter = (node: JSONContent): JSONContent | null => {
        if (UPLOAD_PLACEHOLDER_TYPES.includes(node.type as typeof UPLOAD_PLACEHOLDER_TYPES[number])) {
            return null
        }
        if (node.content) {
            node = {
                ...node,
                content: node.content.map(filter).filter((n): n is JSONContent => n !== null)
            }
        }
        return node
    }

    return filter({ ...doc })
}

export interface TocHeading {
    level: 1 | 2 | 3
    text: string
    index: number
}

/**
 * 从富文本 JSONContent 中提取目录标题列表（H1/H2/H3）。
 *
 * - 按文档顺序遍历，index 与 DOM 中 h1/h2/h3 元素顺序严格一致，
 *   可直接用 querySelectorAll('h1,h2,h3')[index] 定位对应标题元素。
 * - 标题文本通过已封装的 generateText 提取，保证变量节点、格式标记均被正确处理。
 * - 场景：TocBlock 订阅笔记 field block 的 RichTextContentProtocol 内容后调用此函数生成目录条目。
 *
 * @param doc 富文本 JSON（来自 pageStackOfBlockAtom 中笔记 block 的 value 字段）
 * @returns 标题列表，按文档顺序排列
 */
export function collectHeadingsFromJSONContent(doc: JSONContent | null | undefined): TocHeading[] {
    if (!doc) return []

    const headings: TocHeading[] = []

    function walk(node: JSONContent): void {
        if (node.type === 'heading') {
            const level = node.attrs?.level as 1 | 2 | 3
            headings.push({
                level,
                text: generateText(node),
                index: headings.length
            })
            return
        }
        node.content?.forEach(walk)
    }

    walk(doc)
    return headings
}
